<template>
  <div></div>
</template>

<script>
import { mapGetters } from "vuex";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { gmapApi } from "vue2-google-maps";

export default {
  name: "MarkerClusterer",
  computed: {
    ...mapGetters([
      "trackingEnabledEngineers",
      "trackingVisits",
      "trackingActiveEngineer",
      "trackingActiveVisit",
    ]),
    google: gmapApi,
  },
  props: {
    showEngineers: {
      type: Boolean,
      default: true,
    },
    showVisits: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    showEngineers: "debounceInitClusters",
    showVisits: "debounceInitClusters",
    trackingEnabledEngineers: {
      handler: "debounceInitClusters",
      deep: true,
    },
    trackingVisits: {
      handler: "debounceInitClusters",
      deep: true,
    },
  },
  data() {
    return {
      markerClusterer: null,
      clusterDebounceTimeout: null,
      markerDebounceTimeout: null,
      markers: new Map(),
    };
  },
  mounted() {
    this.initClusters();
  },
  beforeDestroy() {
    this.clearClusterDebounce();
    this.clearMarkerClusterer();
  },
  methods: {
    markEngineerActive(engineerUuid) {
      this.clearMarkerDebounce();
      this.markerDebounceTimeout = setTimeout(() => {
        this.activateMarker(`${engineerUuid}-engineer-container`);
      }, 300);
    },
    markVisitActive(visitUuid) {
      this.clearMarkerDebounce();
      this.markerDebounceTimeout = setTimeout(() => {
        this.activateMarker(`${visitUuid}-visit-container`);
      }, 300);
    },
    debounceInitClusters() {
      this.clearClusterDebounce();
      this.clusterDebounceTimeout = setTimeout(() => this.initClusters(), 500);
    },
    clearClusterDebounce() {
      if (this.clusterDebounceTimeout) {
        clearTimeout(this.clusterDebounceTimeout);
        this.clusterDebounceTimeout = null;
      }
    },
    clearMarkerDebounce() {
      if (this.markerDebounceTimeout) {
        clearTimeout(this.markerDebounceTimeout);
        this.markerDebounceTimeout = null;
      }
    },
    async initClusters() {
      if (!this.google?.maps || !this.$parent?.$mapObject) {
        console.warn("Google Maps API or map object not available");
        return;
      }

      this.clearMarkerClusterer();

      try {
        const engineerPromises = this.showEngineers
          ? this.trackingEnabledEngineers.map((row) =>
              this.makeEngineerMarker(row)
            )
          : [];

        const visitPromises = this.showVisits
          ? this.trackingVisits.map((row) => this.makeVisitMarker(row))
          : [];

        const markers = await Promise.all([
          ...engineerPromises,
          ...visitPromises,
        ]);

        this.markerClusterer = new MarkerClusterer({
          map: this.$parent.$mapObject,
          markers: markers.filter(Boolean),
        });

        this.reapplyActiveStates();
      } catch (error) {
        console.error("Failed to initialize marker cluster:", error);
      }
    },
    reapplyActiveStates() {
      if (this.trackingActiveEngineer?.uuid) {
        this.markEngineerActive(this.trackingActiveEngineer?.uuid);
      }
      if (this.trackingActiveVisit?.uuid) {
        this.markVisitActive(this.trackingActiveVisit?.uuid);
      }
    },
    async makeEngineerMarker(row) {
      if (!this.isValidMarkerData(row, ["uuid", "display_name", "position"])) {
        return null;
      }

      try {
        const { AdvancedMarkerElement } = await this.google.maps.importLibrary(
          "marker"
        );
        const {
          uuid,
          display_name: title,
          is_online,
          profile_logo,
          position,
        } = row;

        if (!this.isValidPosition(position)) {
          return null;
        }

        const wrapper = this.createMarkerElement({
          id: `${uuid}-engineer`,
          containerId: `${uuid}-engineer-container`,
          className: "custom-marker engineer-advanced-marker",
          labelText: title,
          labelColor: is_online ? "#28a745" : "#F44336",
          imageUrl: profile_logo,
        });

        const marker = new AdvancedMarkerElement({
          title,
          position,
          content: wrapper,
        });

        this.addMarkerListener(marker, "gmp-click", () =>
          this.engineerClickHandler(row)
        );
        this.markers.set(uuid, marker);

        return marker;
      } catch (error) {
        console.error(
          `Failed to create engineer marker for ${row.uuid}:`,
          error
        );
        return null;
      }
    },
    async makeVisitMarker(row) {
      if (
        !this.isValidMarkerData(row, [
          "uuid",
          "title",
          "barcode",
          "property",
          "status_color",
        ])
      ) {
        return null;
      }

      try {
        const { AdvancedMarkerElement } = await this.google.maps.importLibrary(
          "marker"
        );
        const {
          uuid,
          title,
          barcode,
          status_color,
          property: { position },
        } = row;

        if (!this.isValidPosition(position)) {
          return null;
        }

        const markerIcon = this.$assetURL("media/default/home.svg");
        const colorCode = this.getColorCode(status_color);

        const wrapper = this.createMarkerElement({
          id: `${uuid}-visit`,
          containerId: `${uuid}-visit-container`,
          className: "custom-marker visit-advanced-marker",
          labelText: barcode,
          labelColor: colorCode,
          maskImage: markerIcon,
        });

        const marker = new AdvancedMarkerElement({
          title,
          position,
          content: wrapper,
        });

        this.addMarkerListener(marker, "gmp-click", () =>
          this.visitClickHandler(row)
        );
        this.markers.set(uuid, marker);

        return marker;
      } catch (error) {
        console.error(`Failed to create visit marker for ${row.uuid}:`, error);
        return null;
      }
    },
    createMarkerElement({
      id,
      containerId,
      className,
      labelText,
      labelColor,
      imageUrl,
      maskImage,
    }) {
      const wrapper = document.createElement("div");
      wrapper.id = id;
      wrapper.className = className;

      const container = document.createElement("div");
      container.id = containerId;
      container.className = "marker-container";

      const label = document.createElement("div");
      label.className = "name-label";
      label.style.border = `1px solid ${labelColor}`;
      label.textContent = labelText;

      const pin = document.createElement("div");
      pin.className = "pin";
      pin.style.border = `4px solid ${labelColor}`;
      if (maskImage) {
        pin.style.backgroundColor = labelColor;
      }

      const pinImage = document.createElement("div");
      pinImage.className = "pin-image";
      if (imageUrl) {
        pinImage.style.backgroundImage = `url('${imageUrl}')`;
      } else if (maskImage) {
        pinImage.style.webkitMaskImage = `url('${maskImage}')`;
        pinImage.style.maskImage = `url('${maskImage}')`;
      }

      pin.appendChild(pinImage);
      container.appendChild(label);
      container.appendChild(pin);
      wrapper.appendChild(container);

      return wrapper;
    },
    isValidMarkerData(row, requiredFields) {
      return (
        row &&
        requiredFields.every(
          (field) => row[field] !== undefined && row[field] !== null
        )
      );
    },
    isValidPosition(position) {
      return (
        position &&
        typeof position.lat === "number" &&
        typeof position.lng === "number"
      );
    },
    engineerClickHandler(engineer) {
      this.$emit("click:engineer", engineer);
      this.activateMarker(`${engineer.uuid}-engineer-container`);
    },
    visitClickHandler(visit) {
      this.$emit("click:visit", visit);
      this.activateMarker(`${visit.uuid}-visit-container`);
    },
    clearMarkerClusterer() {
      if (this.markerClusterer) {
        this.markerClusterer.clearMarkers();
        this.markerClusterer.setMap(null);
        this.markerClusterer = null;
      }
      this.markers.clear();
    },
    activateMarker(elementId) {
      const selector = ".marker-container";
      const activeClass = "advanced-marker-active";

      const elements = document.querySelectorAll(selector);
      elements.forEach((el) => el.classList.remove(activeClass));

      if (elementId) {
        const target = document.getElementById(elementId);
        if (target) {
          target.classList.add(activeClass);
        }
      }
    },
    addMarkerListener(marker, event, handler) {
      const listener = marker.addListener(event, handler);
      // Store listener for cleanup (optional, if needed)
      return listener;
    },
  },
};
</script>
